/**
 * Payload builder for the engine's `joinGame` (sit down + buy in).
 *
 * Pure so the two rules below are pinned by unit tests instead of living
 * inside a hook that needs half the app mounted to exercise.
 *
 * ## Rule 1 — the table identity is EXPLICIT, never ambient
 *
 * The engine decides WHICH table to seat you at from the gameID the client
 * sends: `joinGame` (messages/inbound.js) hands its args straight to
 * `userJoinGame`, which resolves the table with
 * `findTableWithWaitlist(usr)` → `WHERE p.gameID = ?`. Only `socketId` is
 * overwritten server-side. So a wrong gameID here seats — or refuses to
 * seat — the player at the wrong table.
 *
 * `baseParams.gameID` is derived from `useTableSelector`, which by design
 * returns the currentTable slice's EMPTY initial state (gameID 0) while
 * `state.tables[gameID]` is unhydrated, and the last-broadcast
 * `state.currentTable` when TableIdContext is empty. With several tables
 * open that is genuinely ambiguous, and it is how a buy-in ended up
 * seat-checking a table the player was already sitting at — the engine
 * answering "You are already seated!" (texas/socket.js) for a table whose
 * felt was showing JOIN TABLE. The caller always knows which table the
 * player acted on, so that id wins.
 *
 * ## Rule 1b — the GAME TYPE is explicit for the same reason
 *
 * The engine routes the join with it twice: `enginepoker.socket[args.game]`
 * picks the texas/omaha handler, and `findTableWithWaitlist` queries
 * `FROM ${game} WHERE gameID = ?`. A stale type therefore either queries the
 * WRONG TABLE LIST (zero rows → the handler returns without emitting anything
 * — the click does nothing, silently) or, when both lists happen to hold the
 * same numeric id, seat-checks a different table entirely. `baseParams.game`
 * comes from the same ambient resolution as the gameID did, so switching
 * between an omaha and a texas table is exactly the setup that arms it
 * (owner repro, 8/25: rapid single↔grid switching, then a buy-in that never
 * registers). The caller knows which table's felt the player acted on — its
 * type wins.
 *
 * ## Rule 2 — an unresolved seat defers to the engine, it is never swallowed
 *
 * `userJoinGame` picks the first free seat itself when no `seat` param is
 * supplied. The client's seat lookup reads the same possibly-unhydrated
 * table state, so when it comes back empty the authoritative answer is the
 * engine's. Sending `seat=0` / `seat=-1` instead would be rejected as an
 * invalid seat, and sending nothing at all used to mean the click did
 * nothing whatsoever.
 */
export type JoinGameParams = Record<string, unknown> & { gameID: unknown; data: string };

export const buildJoinGameParams = (
  baseParams: Record<string, unknown> & { gameID?: unknown; game?: unknown },
  tableId: unknown,
  seatId: unknown,
  amount: unknown,
  gameType?: unknown
): JoinGameParams => {
  const explicit = tableId === null || tableId === undefined ? '' : String(tableId).trim();
  // '0' is the unhydrated sentinel, not a real table.
  const gameID = explicit !== '' && explicit !== '0' ? explicit : baseParams.gameID;

  // Same rule for the type: the caller's explicit knowledge beats the ambient
  // read; an empty string is the unhydrated sentinel (game.game initialises
  // to '') and falls through.
  const explicitType = gameType === null || gameType === undefined ? '' : String(gameType).trim();
  const game = explicitType !== '' ? explicitType : baseParams.game;

  // Only a positive seat is a real seat; anything else (0, -1, NaN, '') means
  // "client could not resolve one" and is omitted so the engine chooses.
  const seat = Number(seatId);
  const seatParam = Number.isFinite(seat) && seat > 0 ? `&seat=${seat}` : '';

  return {
    ...baseParams,
    gameID,
    game,
    data: `${seatParam}&buyin=${amount}&privateTable=0&clubId=0`
  };
};
